
import {Component, Inject, Prop, Vue, Watch} from "vue-property-decorator";
import TransportService from "@/services/TransportService";
import ApiHelper from "@/helpers/api-helper";
import {contarPedido, ESPERA_DE_PRECIOS, EsperaDePrecios} from "@/helpers/espera-de-precios";
import {ValidationObserver, ValidationProvider} from 'vee-validate';
import {opcionalesDelTramo} from "@/helpers/tramo-helper";
import {Cambio, CambiosAlGuardar, opcionalesQueCambian, Quitado, sinCambios, tarifasQueCambian} from "@/helpers/ventana-al-guardar";

	/**
	 * N-287 · Los opcionales elegidos ({id, name, price}) de un transporte, sacados de lo que hay ahora.
	 * Al abrir, `extras` son las filas guardadas (GET del presupuesto): su `id` es el de la fila
	 * y el de catálogo viene en `transport_price_id`. Tras renovar el catálogo, son sus tarifas.
	 */
	function opcionalesElegidos(value: any): any[] {
		const idDeCatalogo = (extra) => extra.transport_price_id != null ? extra.transport_price_id : extra.id;
		return (value.extra_ids || []).map((id) => {
			const elegido = (value.extras || []).find((extra) => idDeCatalogo(extra) == id);
			return { id, name: elegido ? elegido.name : null, price: elegido ? elegido.price : null };
		});
	}

	@Component({
		components: {
			ValidationProvider,
			ValidationObserver
		}
	})
	export default class TransportSelect extends Vue {
		// N-288 · 4A: GUARDAR espera mientras el catálogo de precios está en camino.
		@Inject({ from: ESPERA_DE_PRECIOS, default: null }) private readonly esperaDePrecios!: EsperaDePrecios | null;
		@Prop({ default: null, required: false }) private model;
		@Prop({ default: null, required: false }) private errors;
		@Prop({ default: null, required: false }) private valid;
		@Prop({ default: null, required: true }) private travel_id;
		@Prop({ default: null, required: false }) private state_id;
		@Prop({ default: null, required: true }) private people;
		@Prop({ default: null, required: true }) private days;

		$refs!: {
			obs: InstanceType<typeof ValidationObserver>;
		};

		private items: any[] = [];
		private total = 0;
		private selected = {};
		private value: {
			id: null;
			budget_transport_id: null;
			prices: any[];
			price_ids: any[];
			extras: any[];
			extra_ids: any[];
		};
		private filters: any;
		// N-287: lo que pidió el cliente (o el operador a mano), para remapearlo en cada tramo.
		// `null` = aún no se ha leído del presupuesto: se lee al llegar el primer catálogo.
		private opcionalesPedidos: any[] | null = null;
		// N-288 · T3: el transporte tal como estaba guardado al abrir (las tarifas de `value` se
		// cambian por las del catálogo), para decir en la ventana de GUARDAR lo que cambia.
		private alAbrir: any = null;
		// N-288 · T3: los catálogos (viaje, días y provincia, sin las personas) en los que salía ese
		// transporte: si deja de salir con los mismos, es por los pasajeros y el servidor lo borra.
		private saliaCon: string[] = [];

		@Watch("model")
		model_change(value) {
			this.value = value;
			this.opcionalesPedidos = null;
			this.alAbrir = value && value.id ? JSON.parse(JSON.stringify(value)) : null;
			if (this.value.id) {
				this.$emit('setPrice', this.value);
			}
		}

		@Watch("travel_id")
		travel_id_change(value) {
			this.filters.travel_id = value;
			this.getSelectorList();
		}

		@Watch("state_id")
		state_id_change(value) {
			this.filters.state_id = value;
			this.getSelectorList();
		}

		@Watch("days")
		days_change(value) {
			this.filters.days = value;
			this.getSelectorList();
		}

		@Watch("people")
		people_change(value) {
			this.filters.people = value;
			this.getSelectorList();
		}

		constructor() {
			super();
			this.filters = { travel_id: null, days: null, people: null, state_id: null };
			this.value = {
				id: null,
				budget_transport_id: null,
				prices: [],
				price_ids: [],
				extras: [],
				extra_ids: [],
			};
		}

		mounted() {
			if (this.travel_id) {
				this.filters.travel_id = this.travel_id;
			}
			if (this.state_id) {
				this.filters.state_id = this.state_id;
			}
			if (this.days) {
				this.filters.days = this.days;
			}
			if (this.model.id) {
				this.value = this.model;
				this.alAbrir = JSON.parse(JSON.stringify(this.model));
				this.emitValue();
			}
			if (this.people != null) {
				this.filters.people = this.people;
			}
			this.getSelectorList();
		}

		getSelectorList() {
			if (this.filters.days && this.filters.travel_id && this.filters.people != null) {
				const pedidoCon = JSON.stringify(this.filters);
				contarPedido(this.esperaDePrecios, TransportService.getSelectorList(null, this.filters)
					.then((res) => {
						// N-287: al teclear «30» se pide también el catálogo de «3» + profesores; si esa
						// respuesta llega la última, su tramo no debe pisar al bueno.
						if (pedidoCon !== JSON.stringify(this.filters)) {
							return;
						}
						if (res.status === 200) {
							this.items = res.data.elements;
							const clave = this.claveSinPersonas();
							if (this.alAbrir && this.items.some((transport) => transport.id == this.alAbrir.id)
								&& this.saliaCon.indexOf(clave) < 0) {
								this.saliaCon.push(clave);
							}
							this.items.push({
								id: null,
								name:'Sin Transporte',
								extras: [],
								prices: []
							})
							if (this.value.id) {
								this.renovarTramo();
							}
						}
					})
					.catch((error) => {
						ApiHelper.handleErrors(error, {}, this.$bus);
					}));
			}
		}

		totalPrice(){
			let total = 0;
			this.value.price_ids = [];
			if(this.value.id && this.value.prices){
				for (const price of this.value.prices) {
					this.value.price_ids.push(price['id']);
					total += price['price'];
				}
			}
			this.total = total;
		}

		setSelected(clear_extras = true) {
			this.selected = {};
			if (clear_extras) {
				this.value.extras = [];
				this.value.extra_ids = [];
				this.opcionalesPedidos = [];
			}
			for (const transport of this.items) {
				if (this.value.id == transport.id) {
					this.selected = transport;
					this.value.prices = transport.prices
					this.totalPrice()
					break;
				}
			}
		}

		setExtras(){
			this.value.extras = [];
			for (const extra of this.selected['extras']) {
				if (this.value.extra_ids.indexOf(extra['id'])  > -1) {
					this.value.extras.push(extra);
				}
			}
			// N-287: lo que el operador marca o desmarca a mano pasa a ser lo pedido.
			this.opcionalesPedidos = opcionalesElegidos(this.value);
		}

		/**
		 * N-287: el catálogo se ha renovado (pasajeros, días o provincia). La obligatoria ya la
		 * reelige `setSelected`; los opcionales pasan al del mismo nombre del tramo nuevo, siempre
		 * desde lo PEDIDO, para que un número intermedio sin traslado no lo haga olvidar. Y la
		 * pestaña recibe el importe nuevo.
		 */
		renovarTramo() {
			if (this.opcionalesPedidos === null) {
				this.opcionalesPedidos = opcionalesElegidos(this.value);
			}
			this.setSelected(false);
			// Un transporte que ya no sale en el catálogo se queda como estaba (lo decide el servidor).
			if (!this.selected['id']) {
				return;
			}
			const opcionales = opcionalesDelTramo(this.opcionalesPedidos, this.selected['extras'] || []);
			this.value.extras = opcionales;
			this.value.extra_ids = opcionales.map((opcion) => opcion.id);
			this.emitValue();
		}

		/**
		 * N-288 · T3 · Para la ventana de GUARDAR, si el transporte es el que estaba guardado: se quita
		 * entero si el servidor lo va a borrar (sin tarifa obligatoria para el grupo): cuando sale sin
		 * ninguna, o cuando deja de salir con el mismo viaje, días y provincia (decisión de Bernardo,
		 * 8-oct). Si no sale por la provincia o el viaje, el servidor lo conserva y no se dice nada.
		 * Si sigue, la tarifa obligatoria que pasó a otra de otro precio, y los opcionales pedidos que
		 * el tramo nuevo quitó o cambió. Los importes del transporte ya llegan por persona.
		 */
		cambiosAlGuardar(): CambiosAlGuardar {
			const alAbrir = this.alAbrir;
			// Sin catálogo aún (solo trae «Sin Transporte» cuando llega) no se sabe nada.
			if (!alAbrir || !alAbrir.id || this.value.id != alAbrir.id || !this.items.length) {
				return sinCambios();
			}
			const nombre = alAbrir.name || 'Transporte';
			const quitado: CambiosAlGuardar = {
				quitan: [{ texto: alAbrir.name ? 'Transporte · ' + nombre : nombre, motivo: '', causa: 'personas' }], cambian: [],
			};
			if (!this.selected['id']) {
				return this.saliaCon.indexOf(this.claveSinPersonas()) >= 0 ? quitado : sinCambios();
			}
			// Sale en el catálogo pero sin ninguna tarifa obligatoria para el grupo: el panel manda
			// `price_ids` vacío y el servidor borra el transporte entero.
			if (!(this.value.prices || []).length && (alAbrir.prices || []).length) {
				return quitado;
			}
			const guardadas = (alAbrir.prices || [])
				.map((tarifa) => ({ id: tarifa.transport_price_id, name: tarifa.name, price: tarifa.price }));
			const cambian = tarifasQueCambian(guardadas, this.value.prices || []).map((cambio): Cambio => ({
				texto: nombre + ' · ' + cambio.ahora.name, antes: cambio.antes.price, ahora: cambio.ahora.price, unidad: 'por persona',
			}));
			const opcionales = opcionalesQueCambian(this.opcionalesPedidos || [], this.value.extras || []);
			return {
				quitan: opcionales.quitados.map((pedido): Quitado => ({ texto: nombre + ' · ' + pedido.name, motivo: '', causa: 'personas' })),
				cambian: cambian.concat(opcionales.cambiados.map((cambio): Cambio => ({
					texto: nombre + ' · ' + cambio.ahora.name, antes: cambio.antes.price, ahora: cambio.ahora.price, unidad: 'por persona',
				}))),
			};
		}

		claveSinPersonas() {
			return JSON.stringify([this.filters.travel_id, this.filters.days, this.filters.state_id]);
		}

		emitValue(){
			this.$emit('input', this.value);
			this.$emit('setPrice', this.value);
		}

		async validate() {
			return await this.$refs.obs.validate();
		}
	}
